/* Draft-safe collection ordering. Shopify remains the source of stock, filters,
 * prices and card markup. Shared collection sort orders are never modified. */
(() => {
  if (customElements.get('ux-stock-last')) return;

  // Slice a virtual collection made of two independently paginated streams.
  function stockSlices(total, available, size, page) {
    const start = (page - 1) * size;
    const end = Math.min(total, start + size);
    return [
      { stock: '1', start, end: Math.min(end, available) },
      { stock: '0', start: Math.max(0, start - available), end: end - available },
    ].filter(slice => slice.end > slice.start);
  }

  class StockLast extends HTMLElement {
    connectedCallback() {
      // Facets updates the URL in the same turn as replacing the grid.
      queueMicrotask(() => { if (this.isConnected) this.sort(); });
    }

    disconnectedCallback() {
      this.controller?.abort();
      clearTimeout(this.timer);
    }

    async sort() {
      const grid = this.querySelector('ul#product-grid');
      const total = Number(this.dataset.total);
      const size = Number(this.dataset.pageSize);
      const page = Number(this.dataset.page);
      if (!grid || !total || !size || !page) return;
      const base = new URL(location.href);
      const availability = [...new Set(base.searchParams.getAll('filter.v.availability'))];
      // Respect a visitor explicitly choosing only available or only sold out.
      if (availability.length === 1 && ['0', '1'].includes(availability[0])) {
        this.dataset.stockOrder = 'filtered';
        return;
      }
      base.searchParams.delete('filter.v.availability');
      base.searchParams.delete('page');
      base.searchParams.delete('sections');
      base.searchParams.set('section_id', this.dataset.section);
      this.controller = new AbortController();
      this.timer = setTimeout(() => this.controller.abort(), 10000);
      this.setAttribute('aria-busy', 'true');
      this.dataset.stockOrder = 'loading';
      const requests = new Map();
      const getPage = (stock, number) => {
        const key = `${stock}:${number}`;
        if (!requests.has(key)) requests.set(key, (async () => {
          const url = new URL(base);
          url.searchParams.set('filter.v.availability', stock);
          url.searchParams.set('page', String(number));
          const response = await fetch(url, { signal: this.controller.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Collection response unavailable');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const wrapper = doc.querySelector('ux-stock-last');
          if (!wrapper || wrapper.dataset.section !== this.dataset.section) throw new Error('Unexpected collection response');
          const count = Number(wrapper.dataset.total);
          const cards = [...wrapper.querySelectorAll('#product-grid > li[data-product-id]')];
          if (!Number.isInteger(count) || count < 0 || Number(wrapper.dataset.pageSize) !== size ||
              cards.some(card => card.dataset.available !== (stock === '1' ? 'true' : 'false'))) {
            throw new Error('Availability filter not supported');
          }
          return { count, cards };
        })());
        return requests.get(key);
      };
      try {
        const first = await getPage('1', 1);
        const available = first.count;
        if (available > total) throw new Error('Collection changed during load');
        const slices = stockSlices(total, available, size, page);
        const chunks = await Promise.all(slices.map(async slice => {
          const firstPage = Math.floor(slice.start / size) + 1;
          const lastPage = Math.floor((slice.end - 1) / size) + 1;
          const pages = await Promise.all(Array.from({ length: lastPage - firstPage + 1 }, (_, index) => getPage(slice.stock, firstPage + index)));
          const expected = slice.stock === '1' ? available : total - available;
          if (pages.some(result => result.count !== expected)) throw new Error('Collection changed during load');
          return pages.flatMap(result => result.cards).slice(slice.start % size, slice.start % size + slice.end - slice.start);
        }));
        const cards = chunks.flat();
        if (cards.length !== Math.min(size, total - (page - 1) * size) ||
            new Set(cards.map(card => card.dataset.productId)).size !== cards.length) throw new Error('Incomplete collection page');
        if (!this.isConnected || this.controller.signal.aborted) return;
        // Fetched animation classes have no observer in this document.
        cards.forEach(card => {
          card.classList.remove('scroll-trigger', 'animate--slide-in');
          card.querySelectorAll('.scroll-trigger').forEach(node => node.classList.add('scroll-trigger--cancel'));
        });
        grid.replaceChildren(...cards);
        this.dataset.availableCount = String(available);
        this.dataset.stockOrder = 'ready';
      } catch (error) {
        // Fail open: retain the complete native Shopify page and its links.
        this.dataset.stockOrder = 'native-fallback';
        if (error.name !== 'AbortError') console.warn('[Hyashop] Stock ordering retained native results:', error.message);
      } finally {
        clearTimeout(this.timer);
        this.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('ux-stock-last', StockLast);
})();
